import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip, createBooking, getTripBookings, acceptBooking, declineBooking } from '../services/api';
import { useAuth } from '../context/AuthContext';

const TripDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [amountOffered, setAmountOffered] = useState(0);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [showBookings, setShowBookings] = useState(false);
  const [bookingsLoading, setBookingsLoading] = useState(false);

  useEffect(() => {
    const fetchTrip = async () => {
      try {
        const res = await getTrip(id);
        setTrip(res.data.trip);
        setAmountOffered(res.data.trip.price_suggestion || 0);
      } catch (err) {
        setError('Trip not found.');
      } finally {
        setLoading(false);
      }
    };
    fetchTrip();
  }, [id]);

  const handleBooking = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!user.is_verified) {
      navigate('/membership');
      return;
    }
    setBookingLoading(true);
    setBookingError('');
    try {
      await createBooking({ trip_id: id, amount_offered: amountOffered });
      setBookingSuccess(true);
    } catch (err) {
      setBookingError(err.response?.data?.error || 'Booking failed. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleManageBookings = async () => {
    setShowBookings(!showBookings);
    if (!showBookings) {
      setBookingsLoading(true);
      try {
        const res = await getTripBookings(id);
        setBookings(res.data.bookings);
      } catch (err) {
        console.error('Failed to fetch bookings');
      } finally {
        setBookingsLoading(false);
      }
    }
  };

  const handleAccept = async (bookingId) => {
    try {
      await acceptBooking(bookingId);
      const res = await getTripBookings(id);
      setBookings(res.data.bookings);
      const tripRes = await getTrip(id);
      setTrip(tripRes.data.trip);
    } catch (err) {
      console.error('Failed to accept booking');
    }
  };

  const handleDecline = async (bookingId) => {
    try {
      await declineBooking(bookingId);
      const res = await getTripBookings(id);
      setBookings(res.data.bookings);
    } catch (err) {
      console.error('Failed to decline booking');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/New_York'
    });
  };

  if (loading) return <div style={styles.loading}>Loading trip...</div>;
  if (error) return <div style={styles.error}>{error}</div>;
  if (!trip) return null;

  const isDriver = user && trip.driver_id === user.id;

  return (
    <div style={styles.container}>
      <button style={styles.backBtn} onClick={() => navigate(-1)}>
        ← Back
      </button>

      <div style={styles.card}>
        <div style={styles.route}>
          {trip.origin_address} → {trip.destination_address}
        </div>

        <div style={styles.details}>
          <div style={styles.detail}>
            <span style={styles.label}>Departure</span>
            <span style={styles.value}>{formatDate(trip.departure_time)}</span>
          </div>
          <div style={styles.detail}>
            <span style={styles.label}>Seats Available</span>
            <span style={styles.value}>{trip.seats_available}</span>
          </div>
          <div style={styles.detail}>
            <span style={styles.label}>Status</span>
            <span style={styles.value}>{trip.status}</span>
          </div>
          <div style={styles.detail}>
            <span style={styles.label}>Price</span>
            <span style={styles.value}>
              {trip.price_suggestion === '0.00' || trip.price_suggestion === 0
                ? 'Free'
                : `$${trip.price_suggestion}`}
              {trip.price_negotiable && ' · Negotiable'}
            </span>
          </div>
        </div>

        {trip.notes && (
          <div style={styles.notes}>{trip.notes}</div>
        )}

        <div style={styles.driverSection}>
          <div style={styles.driverLabel}>Your Driver</div>
          <div style={styles.driverInfo}>
            <div style={styles.avatar}>
              {trip.first_name?.[0]}{trip.last_name?.[0]}
            </div>
            <div>
              <div style={styles.driverName}>{trip.first_name} {trip.last_name}</div>
              <div style={styles.rating}>★ {trip.avg_rating || '0.0'}</div>
            </div>
          </div>
        </div>

        {isDriver ? (
          <div style={styles.driverActions}>
            <p style={styles.driverNote}>This is your trip.</p>
            <button style={styles.manageBtn} onClick={handleManageBookings}>
              {showBookings ? 'Hide Bookings' : 'Manage Bookings'}
            </button>

            {showBookings && (
              <div style={styles.bookingsList}>
                {bookingsLoading && <p>Loading bookings...</p>}
                {!bookingsLoading && bookings.length === 0 && (
                  <p style={styles.noBookings}>No booking requests yet.</p>
                )}
                {bookings.map(booking => (
                  <div key={booking.id} style={styles.bookingCard}>
                    <div style={styles.bookingInfo}>
                      <strong>{booking.first_name} {booking.last_name}</strong>
                      <span style={{
                        ...styles.statusBadge,
                        background: booking.status === 'accepted' ? '#e8f5e9' :
                                   booking.status === 'declined' ? '#ffebee' : '#fff3e0',
                        color: booking.status === 'accepted' ? '#2e7d32' :
                               booking.status === 'declined' ? '#c62828' : '#e65100'
                      }}>
                        {booking.status}
                      </span>
                    </div>
                    {booking.status === 'pending' && (
                      <div style={styles.bookingActions}>
                        <button
                          style={styles.acceptBtn}
                          onClick={() => handleAccept(booking.id)}
                        >
                          Accept
                        </button>
                        <button
                          style={styles.declineBtn}
                          onClick={() => handleDecline(booking.id)}
                        >
                          Decline
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          trip.status === 'active' && trip.seats_available > 0 && (
            <div style={styles.bookingSection}>
              {bookingSuccess ? (
                <div style={styles.success}>
                  ✓ Booking request sent! The driver will review your request.
                </div>
              ) : (
                <>
                  {trip.price_negotiable && (
                    <div style={styles.offerSection}>
                      <label style={styles.label}>Your Offer ($)</label>
                      <input
                        style={styles.offerInput}
                        type="number"
                        min="0"
                        step="0.01"
                        value={amountOffered}
                        onChange={(e) => setAmountOffered(e.target.value)}
                      />
                    </div>
                  )}
                  {bookingError && <div style={styles.bookingError}>{bookingError}</div>}
                  <button
                    style={styles.bookBtn}
                    onClick={handleBooking}
                    disabled={bookingLoading}
                  >
                    {bookingLoading ? 'Sending...' : 'Request to Book'}
                  </button>
                </>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '700px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#4fc3f7',
    fontSize: '16px',
    cursor: 'pointer',
    marginBottom: '20px',
    padding: 0,
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  route: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1a1a2e',
    marginBottom: '24px',
  },
  details: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '24px',
  },
  detail: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    fontSize: '12px',
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  value: {
    fontSize: '16px',
    color: '#1a1a2e',
    fontWeight: '500',
  },
  notes: {
    background: '#f5f5f5',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px',
    color: '#555',
    fontSize: '14px',
  },
  driverSection: {
    borderTop: '1px solid #eee',
    paddingTop: '24px',
    marginBottom: '24px',
  },
  driverLabel: {
    fontSize: '12px',
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
  },
  driverInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  avatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: '#4fc3f7',
    color: '#1a1a2e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
  },
  driverName: {
    fontWeight: '600',
    color: '#1a1a2e',
    fontSize: '16px',
  },
  rating: {
    color: '#f59e0b',
    fontSize: '14px',
  },
  driverActions: {
    borderTop: '1px solid #eee',
    paddingTop: '24px',
  },
  driverNote: {
    color: '#666',
    marginBottom: '12px',
  },
  manageBtn: {
    padding: '12px 32px',
    background: '#1a1a2e',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  bookingsList: {
    marginTop: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  noBookings: {
    color: '#666',
    textAlign: 'center',
    padding: '20px',
    background: '#f5f5f5',
    borderRadius: '8px',
  },
  bookingCard: {
    background: '#f9f9f9',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  bookingInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
  },
  bookingActions: {
    display: 'flex',
    gap: '8px',
  },
  acceptBtn: {
    padding: '8px 20px',
    background: '#4caf50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  declineBtn: {
    padding: '8px 20px',
    background: '#f44336',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  bookingSection: {
    borderTop: '1px solid #eee',
    paddingTop: '24px',
  },
  offerSection: {
    marginBottom: '16px',
  },
  offerInput: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '16px',
    width: '150px',
    marginTop: '6px',
  },
  bookingError: {
    background: '#ffebee',
    color: '#c62828',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '12px',
    fontSize: '14px',
  },
  bookBtn: {
    width: '100%',
    padding: '14px',
    background: '#4fc3f7',
    color: '#1a1a2e',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  success: {
    background: '#e8f5e9',
    color: '#2e7d32',
    padding: '16px',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '500',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
  },
  error: {
    textAlign: 'center',
    padding: '40px',
    color: '#c62828',
  },
};

export default TripDetail;